/**
 * Integrations: what the platform connects to, one card per connector
 * (task 69; Stories 3, 10, 18).
 *
 * The list is the connector registry, read from the API, so the page
 * cannot drift from what the ingestion Lambda actually knows. Each card
 * says how the integration connects, what it declares it could do (Story
 * 18 — declared, not enabled), and how many visible Clients have it
 * connected. Managing it — credentials, per-Client health — is the detail
 * page behind the card. Before this page those controls hid in a
 * per-row Actions column on the Clients page, which is the discoverability
 * bug task 69 fixes.
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import {
  listIntegrations,
  listTenants,
  type ClientTenant,
  type IntegrationSummary,
} from '@bluesphere/api-client';

import { useAccessToken } from '../auth/useAccessToken';
import { CapabilitiesLine } from '../components/SourceHealth';
import { EmptyState, ErrorState, Loading } from '../components/states';
import { INTEGRATION_PAGES, connectionState, isIntegrationName } from '../lib/integrations';
import { sourceLabel } from '@bluesphere/mdr-alert-panel';

export function Integrations() {
  const idToken = useAccessToken();
  const integrations = useQuery({
    queryKey: ['integrations'],
    queryFn: () => listIntegrations(idToken),
    enabled: Boolean(idToken),
    staleTime: 5 * 60_000, // the registry changes with a deploy, not with time
  });
  const tenants = useQuery({
    queryKey: ['tenants'],
    queryFn: async () => (await listTenants(idToken)).tenants,
    enabled: Boolean(idToken),
    refetchInterval: 15_000,
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Integrations</h1>
        <p className="mt-1 max-w-3xl text-sm text-white/60">
          The systems BlueSphere MDR reads from, and for each one, which Clients are connected and
          how. Credentials are created, rotated and removed here, per Client.
        </p>
      </div>

      {integrations.isPending && <Loading height="h-40" />}
      {integrations.isError && (
        <ErrorState
          title="Could not load the integrations"
          error={integrations.error}
          onRetry={() => void integrations.refetch()}
        />
      )}
      {integrations.data && integrations.data.integrations.length === 0 && (
        <EmptyState title="No integrations" body="The connector registry is empty." />
      )}
      {integrations.data && integrations.data.integrations.length > 0 && (
        <div className="grid gap-6 md:grid-cols-2">
          {integrations.data.integrations.map((integration) => (
            <IntegrationCard
              key={integration.name}
              integration={integration}
              tenants={tenants.data ?? []}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function IntegrationCard({
  integration,
  tenants,
}: {
  integration: IntegrationSummary;
  tenants: ClientTenant[];
}) {
  const { name, manifest } = integration;
  // The API's registry can name an integration this build has no page for
  // (a connector added after the SPA shipped); the card degrades rather
  // than crashes.
  const known = isIntegrationName(name);
  const page = known ? INTEGRATION_PAGES[name] : null;
  const live = tenants.filter((t) => t.status !== 'revoked');
  const connected = known
    ? live.filter((t) => connectionState(t, name) === 'connected').length
    : 0;

  return (
    <Link
      to="/integrations/$name"
      params={{ name }}
      className="bs-card block p-5 transition-shadow hover:shadow-cardHover"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-xl font-bold text-bluesphere-textOnLight">{sourceLabel(name)}</h2>
          <p className="mt-1 text-sm text-bluesphere-textSecondary">
            {page?.description ?? 'A connector the platform knows but this console has no page for yet.'}
          </p>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-3xl font-bold leading-none tabular-nums text-bluesphere-primary">
            {connected}
          </div>
          <div className="mt-1 text-xs text-bluesphere-muted">
            of {live.length} Client{live.length === 1 ? '' : 's'} connected
          </div>
        </div>
      </div>
      <div className="mt-4 border-t border-bluesphere-border pt-3">
        <CapabilitiesLine manifest={manifest} />
        <p className="mt-2 text-xs text-bluesphere-muted">
          Connects by {connectsByLabel(page?.connectsBy)} · manage →
        </p>
      </div>
    </Link>
  );
}

function connectsByLabel(connectsBy: string | undefined): string {
  switch (connectsBy) {
    case 'consent':
      return 'admin consent';
    case 'enable':
      return 'enabling per Client';
    case 'mapping':
      return 'platform credential and Client mapping';
    case 'key':
      return 'an ingest key per source';
    default:
      return 'API token';
  }
}
